import React, { useState, useCallback } from 'react';
import {
  makeStyles,
  Card,
  CardHeader,
  CardContent,
  Collapse,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import ReorderIcon from '@material-ui/icons/Reorder';
import clsx from 'clsx';

const useStyles = makeStyles(theme => ({
  root: {
    marginBottom: theme.spacing(2),
    transition: 'box-shadow 0.2s, border-color 0.2s',
  },
  dragOver: {
    boxShadow: `0 0 0 2px ${theme.palette.primary.main}`,
    backgroundColor: theme.palette.action.hover,
  },
  header: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  expand: {
    transform: 'rotate(0deg)',
    marginLeft: 'auto',
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.shortest,
    }),
  },
  expandOpen: {
    transform: 'rotate(180deg)',
  },
  headerAction: {
    marginTop: 0,
    marginRight: 0,
    alignSelf: 'center',
  },
  content: {
    paddingTop: 0,
  },
  emptyContent: {
    padding: theme.spacing(2),
    textAlign: 'center',
    color: theme.palette.text.secondary,
  },
}));

interface CollapsibleSectionProps {
  title: string;
  sectionKey: string;
  children: React.ReactNode;
  defaultCollapsed?: boolean;
  onHide?: () => void;
  onReorder?: () => void;
  onLinkDrop?: (url: string, title?: string) => void;
  isEmpty?: boolean;
  emptyMessage?: string;
  action?: React.ReactNode;
  persistentAction?: React.ReactNode; // Action that's always visible (not just in edit mode)
  editMode?: boolean;
}

export const CollapsibleSection = ({
  title,
  sectionKey: _sectionKey,
  children,
  defaultCollapsed = false,
  onHide,
  onReorder,
  onLinkDrop,
  isEmpty = false,
  emptyMessage,
  action,
  persistentAction,
  editMode = false,
}: CollapsibleSectionProps) => {
  // sectionKey can be used for persistence in the future
  const classes = useStyles();
  const [expanded, setExpanded] = useState(!defaultCollapsed);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const handleExpandClick = useCallback(() => {
    setExpanded(prev => !prev);
  }, []);

  const handleMenuClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      setMenuAnchor(event.currentTarget);
    },
    [],
  );

  const handleMenuClose = useCallback(() => {
    setMenuAnchor(null);
  }, []);

  const handleHide = useCallback(() => {
    handleMenuClose();
    onHide?.();
  }, [onHide, handleMenuClose]);

  const handleReorder = useCallback(() => {
    handleMenuClose();
    onReorder?.();
  }, [onReorder, handleMenuClose]);

  const handleDragOver = useCallback(
    (event: React.DragEvent) => {
      if (!editMode || !onLinkDrop) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = 'copy';
      setIsDragOver(true);
    },
    [editMode, onLinkDrop],
  );

  const handleDragLeave = useCallback(() => {
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (event: React.DragEvent) => {
      if (!editMode || !onLinkDrop) return;
      event.preventDefault();
      setIsDragOver(false);

      // Try to get URL from various data types
      let url = event.dataTransfer.getData('text/uri-list');
      if (!url) {
        url = event.dataTransfer.getData('text/plain');
      }

      // Clean up the URL (remove any extra whitespace or newlines)
      url = url?.trim().split('\n')[0];

      // Try to extract title from HTML (anchor tag text)
      let title: string | undefined;
      const html = event.dataTransfer.getData('text/html');
      if (html) {
        // Parse HTML to extract link text
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const anchor = doc.querySelector('a');
        if (anchor?.textContent) {
          title = anchor.textContent.trim();
        }
      }

      // Fallback: if no title from HTML, use text/plain if it's different from URL
      if (!title) {
        const plainText = event.dataTransfer.getData('text/plain')?.trim();
        if (plainText && plainText !== url && !plainText.startsWith('http')) {
          title = plainText;
        }
      }

      if (url && (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('/'))) {
        onLinkDrop(url, title);
      }
    },
    [editMode, onLinkDrop],
  );

  return (
    <Card
      className={clsx(classes.root, { [classes.dragOver]: isDragOver })}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <CardHeader
        className={classes.header}
        onClick={handleExpandClick}
        title={
          <Typography variant="h6" component="span">
            {title}
          </Typography>
        }
        action={
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {persistentAction}
            {editMode && action}
            {editMode && (
              <IconButton
                aria-label="section options"
                onClick={handleMenuClick}
                size="small"
              >
                <MoreVertIcon />
              </IconButton>
            )}
            <IconButton
              className={clsx(classes.expand, {
                [classes.expandOpen]: expanded,
              })}
              aria-expanded={expanded}
              aria-label="show more"
              size="small"
            >
              <ExpandMoreIcon />
            </IconButton>
          </div>
        }
        classes={{ action: classes.headerAction }}
      />
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <CardContent className={classes.content}>
          {isEmpty ? (
            <Typography className={classes.emptyContent}>
              {emptyMessage || 'No content available'}
            </Typography>
          ) : (
            children
          )}
        </CardContent>
      </Collapse>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        {onReorder && (
          <MenuItem onClick={handleReorder}>
            <ReorderIcon
              fontSize="small"
              style={{ marginRight: 8 }}
            />
            Reorder Groups
          </MenuItem>
        )}
        {onHide && (
          <MenuItem onClick={handleHide}>
            <VisibilityOffIcon
              fontSize="small"
              style={{ marginRight: 8 }}
            />
            Hide Section
          </MenuItem>
        )}
      </Menu>
    </Card>
  );
};
